import {
	type ComponentType,
	useMemo,
	useRef,
	useImperativeHandle,
	forwardRef,
	type ReactNode,
} from 'react';
import Collapse from '@liquidweb-dev/undercurrent/Collapse';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import ChevronIcon from '@liquidweb-dev/undercurrent/icons/animated/Chevron';
import { styled } from '@liquidweb-dev/undercurrent/styles';
import type { SxProps, Theme } from '@liquidweb-dev/undercurrent/styles';
import type { LucideSvgProps } from '@liquidweb-dev/undercurrent/types/svgTypes';

const buttonHoverStyles: SxProps<Theme> = {
	bgcolor: (theme) => theme.vars.palette.uc.bg.medium,
	outline: 'none',
};

const buttonStyles: SxProps<Theme> = {
	cursor: 'pointer',
	height: (theme) => theme.size.sm,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'stretch',
	color: (theme) => theme.vars.palette.uc.text.weak,
	bgcolor: 'transparent',
	paddingY: 50,
	paddingX: 100,
	marginLeft: 50,
	border: 'none',
	borderRadius: (theme) => theme.borderRadius.roundedSm,
	gap: 100,
	':hover': buttonHoverStyles,
	':active': buttonHoverStyles,
	':focus-visible': {
		outlineOffset: '2px',
		outlineWidth: '2px',
		outlineStyle: 'solid',
		outlineColor: (theme) => theme.vars.palette.uc.highlight.border.main,
	},
};

const SSection = styled('section')({});
const SButton = styled('button')({});

export interface NavSectionPropsI {
	children: ReactNode;
	label?: string;
	onToggle?: () => void;
	open?: boolean;
	LabelIcon?: ComponentType<LucideSvgProps>;
}

export const NavSection = forwardRef((props: NavSectionPropsI, ref) => {
	const { label, LabelIcon, children, open, onToggle, ...sectionProps } = props;

	const sectionRef = useRef<HTMLElement | null>(null);
	useImperativeHandle(ref, () => sectionRef.current);

	const ariaId = useMemo(() => {
		if (!label) return undefined;
		return `nav_${label}`;
	}, [label]);

	return (
		<SSection {...sectionProps} ref={sectionRef}>
			{label && (
				<SButton
					onClick={onToggle}
					sx={buttonStyles}
					aria-expanded={open}
					aria-controls={ariaId}
				>
					{LabelIcon && (
						<LabelIcon
							sx={({ size }) => ({ height: size.xS, width: size.xS })}
						/>
					)}
					<Typography variant="label-lg" fontWeight={500}>
						{label}
					</Typography>
					<ChevronIcon open={open} sx={{ height: '16px', width: '16px' }} />
				</SButton>
			)}
			<Collapse in={open || !label} timeout={200} easing="linear" id={ariaId}>
				<Stack
					flexDirection="column"
					justifyContent="space-between"
					alignItems="stretch"
					useFlexGap
					paddingTop={50}
				>
					{children}
				</Stack>
			</Collapse>
		</SSection>
	);
});
NavSection.displayName = 'NavSection';

export default NavSection;
